import { useState, useEffect, useRef } from 'react';
import { Dices, Play, Square, Clock, Trophy, RotateCcw, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DEFAULT_ROULETTE_VALUES,
  NO_ROULETTE_CANDIDATES,
  drawRouletteRound,
  saveRouletteConfig,
  subscribeToDJRouletteRounds,
  type RouletteConfig,
  type RouletteConfigValues,
  type RouletteRound,
} from '../../services/roulette';
import { PRIZE_OPTIONS, prizeNeedsText } from './prizeOptions';

interface RoulettePanelProps {
  eventId: string;
  djId: string;
  config: RouletteConfig | null;
}

function valuesFromConfig(config: RouletteConfig | null): RouletteConfigValues {
  if (!config) return DEFAULT_ROULETTE_VALUES;
  return {
    enabled: config.enabled,
    mode: config.mode,
    intervalMinutes: config.intervalMinutes,
    prizeType: config.prizeType,
    prizeCustomText: config.prizeCustomText,
    allowRepeatWinners: config.allowRepeatWinners,
    playNext: config.playNext,
    nextDrawAt: config.nextDrawAt,
  };
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function ToggleRow({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

export default function RoulettePanel({ eventId, djId, config }: RoulettePanelProps) {
  const [values, setValues] = useState<RouletteConfigValues>(() => valuesFromConfig(config));
  const [customText, setCustomText] = useState(config?.prizeCustomText ?? '');
  const [spinning, setSpinning] = useState(false);
  const [rounds, setRounds] = useState<RouletteRound[]>([]);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const loadedFromConfigRef = useRef(config !== null);

  useEffect(() => {
    if (!config) return;
    if (!loadedFromConfigRef.current) {
      loadedFromConfigRef.current = true;
      setValues(valuesFromConfig(config));
      setCustomText(config.prizeCustomText ?? '');
      return;
    }
    setValues((current) => ({ ...current, enabled: config.enabled, nextDrawAt: config.nextDrawAt }));
  }, [config]);

  useEffect(() => {
    const unsubscribe = subscribeToDJRouletteRounds(eventId, setRounds, () => {
      toast.error("Couldn't load roulette history");
    });
    return () => unsubscribe();
  }, [eventId]);

  const running = values.enabled && values.mode === 'recurring';

  useEffect(() => {
    if (!running || values.nextDrawAt === null) {
      setSecondsRemaining(0);
      return;
    }
    const nextDrawAt = values.nextDrawAt;
    const update = () => setSecondsRemaining(Math.max(0, Math.ceil((nextDrawAt - Date.now()) / 1000)));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [running, values.nextDrawAt]);

  const persist = async (patch: Partial<RouletteConfigValues>) => {
    const next = { ...values, ...patch };
    setValues(next);
    try {
      await saveRouletteConfig(eventId, djId, next);
    } catch (error) {
      console.error('[Roulette] Saving settings failed:', error);
      toast.error("Couldn't save roulette settings");
    }
  };

  const saveCustomText = () => {
    const trimmed = customText.trim();
    const nextText = trimmed ? trimmed : null;
    if (nextText === values.prizeCustomText) return;
    void persist({ prizeCustomText: nextText });
  };

  const handleSpin = async () => {
    if (spinning) return;
    setSpinning(true);
    try {
      const trimmed = customText.trim();
      const round = await drawRouletteRound(eventId, djId, { ...values, prizeCustomText: trimmed ? trimmed : null });
      toast.success(`Spotlight: "${round.songTitle}" — winner: ${round.winnerNickname} (${round.entrantCount} in the draw)`);
    } catch (error) {
      if (error instanceof Error && error.message === NO_ROULETTE_CANDIDATES) {
        toast.error('No queued song has voters yet');
      } else {
        console.error('[Roulette] Spin failed:', error);
        toast.error("Couldn't spin the roulette");
      }
    } finally {
      setSpinning(false);
    }
  };

  const handleStart = async () => {
    await persist({ enabled: true, nextDrawAt: Date.now() + values.intervalMinutes * 60000 });
    toast.success('Roulette started');
  };

  const handleStop = async () => {
    await persist({ enabled: false, nextDrawAt: null });
    toast.success('Roulette stopped');
  };

  const optionClass = (selected: boolean) => `flex-1 px-3 py-2 text-sm rounded-lg border ${
    selected
      ? 'border-primary bg-primary/10 text-primary font-medium'
      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
  }`;

  return (
    <div className="space-y-4">
      {running && (
        <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
          </span>
          <span className="text-sm font-medium text-green-700 dark:text-green-300">Roulette running</span>
          <span className="ml-auto flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
            <Clock className="w-4 h-4" />
            {secondsRemaining > 0 ? formatTime(secondsRemaining) : 'Spinning…'}
          </span>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Mode</label>
          <div className="flex gap-2">
            <button onClick={() => persist({ mode: 'one_time', enabled: false, nextDrawAt: null })} disabled={running} className={optionClass(values.mode === 'one_time')}>
              One-time
            </button>
            <button onClick={() => persist({ mode: 'recurring' })} disabled={running} className={optionClass(values.mode === 'recurring')}>
              <div className="flex items-center justify-center gap-1">
                <RotateCcw className="w-3 h-3" />
                Recurring
              </div>
            </button>
          </div>
        </div>

        {values.mode === 'recurring' && (
          <div>
            <label htmlFor="roulette-interval" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              Spin every (minutes)
            </label>
            <input
              id="roulette-interval"
              type="number"
              min={1}
              max={60}
              value={values.intervalMinutes}
              disabled={running}
              onChange={(e) => persist({ intervalMinutes: Math.min(60, Math.max(1, parseInt(e.target.value) || 1)) })}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm disabled:opacity-60"
            />
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Prize</label>
          <div className="grid grid-cols-2 gap-2">
            {PRIZE_OPTIONS.map((option) => (
              <button
                key={option.type}
                onClick={() => persist({ prizeType: option.type })}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border ${
                  values.prizeType === option.type
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <span>{option.icon}</span>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {prizeNeedsText(values.prizeType) && (
          <div>
            <label htmlFor="roulette-prize-text" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              {values.prizeType === 'shoutout' ? 'Shoutout message' : 'Prize description'}
            </label>
            <input
              id="roulette-prize-text"
              type="text"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              onBlur={saveCustomText}
              placeholder={values.prizeType === 'shoutout' ? 'e.g., "Congrats to our winner!"' : 'e.g., "Free drink at the bar"'}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>
        )}

        <ToggleRow
          label="Allow repeat winners"
          hint="Same attendee can win more than one spin"
          checked={values.allowRepeatWinners}
          onChange={() => persist({ allowRepeatWinners: !values.allowRepeatWinners })}
        />
        <ToggleRow
          label="Play the spotlighted song next"
          hint="Moves the song to the top of your queue, below locked songs"
          checked={values.playNext}
          onChange={() => persist({ playNext: !values.playNext })}
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSpin}
          disabled={spinning}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 font-medium"
        >
          <Dices className="w-4 h-4" />
          {spinning ? 'Spinning…' : 'Spin Now'}
        </button>
        {values.mode === 'recurring' && !running && (
          <button
            onClick={handleStart}
            className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
          >
            <Play className="w-4 h-4" />
            Start
          </button>
        )}
        {running && (
          <button
            onClick={handleStop}
            className="flex items-center gap-2 px-4 py-2.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50"
          >
            <Square className="w-4 h-4" />
            Stop
          </button>
        )}
      </div>

      {rounds.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <Trophy className="w-4 h-4" />
            History ({rounds.length} {rounds.length === 1 ? 'spin' : 'spins'})
          </h3>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {rounds.map((round) => (
              <div key={round.id} className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-900 dark:text-white truncate">"{round.songTitle}"</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                    {new Date(round.drawnAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="truncate">Winner: {round.winnerNickname}</span>
                  <span className="flex items-center gap-1 flex-shrink-0">
                    <Users className="w-3 h-3" />
                    {round.entrantCount}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
